import { PATH_NUMBER } from './path-number';

export interface PathPoint {
  x: number;
  y: number;
  /** Tangent angle in radians. */
  tangent: number;
}

export interface PathSampler {
  readonly totalLength: number;
  readonly closed: boolean;
  pointAtLength(s: number): PathPoint;
}

interface Segment {
  /** Cumulative length AT START of this segment. */
  startLength: number;
  /** Cumulative length AT END of this segment. */
  endLength: number;
  /** Polyline samples: [x, y][] including start and end. */
  samples: Array<readonly [number, number]>;
}

const PATH_TOKEN = new RegExp(`[MLCZ]|${PATH_NUMBER}`, 'g');

function parsePathCommands(d: string): Array<{ cmd: string; args: number[] }> {
  const tokens = d.match(PATH_TOKEN) || [];
  const out: Array<{ cmd: string; args: number[] }> = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (/[MLCZ]/.test(t)) {
      const cmd = t;
      i++;
      const argN = cmd === 'M' || cmd === 'L' ? 2 : cmd === 'C' ? 6 : 0;
      const args: number[] = [];
      for (let k = 0; k < argN; k++, i++) args.push(parseFloat(tokens[i]));
      out.push({ cmd, args });
    } else {
      i++;
    }
  }

  return out;
}

function distance(p1: readonly [number, number], p2: readonly [number, number]): number {
  return Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
}

function buildSegments(d: string, closed: boolean, samplesPerCubic: number): { segments: Segment[]; totalLength: number } {
  const cmds = parsePathCommands(d);
  const segments: Segment[] = [];
  let cursor: [number, number] = [0, 0];
  let pathStart: [number, number] = [0, 0];
  let cumulative = 0;

  for (const { cmd, args } of cmds) {
    if (cmd === 'M') {
      cursor = [args[0], args[1]];
      pathStart = [...cursor] as [number, number];
    } else if (cmd === 'L') {
      const next: [number, number] = [args[0], args[1]];
      const len = distance(cursor, next);
      segments.push({
        startLength: cumulative,
        endLength: cumulative + len,
        samples: [cursor, next],
      });
      cumulative += len;
      cursor = next;
    } else if (cmd === 'C') {
      const c1: [number, number] = [args[0], args[1]];
      const c2: [number, number] = [args[2], args[3]];
      const end: [number, number] = [args[4], args[5]];
      const samples: Array<[number, number]> = [];
      for (let k = 0; k <= samplesPerCubic; k++) {
        const tt = k / samplesPerCubic;
        const omt = 1 - tt;
        const x = omt ** 3 * cursor[0] + 3 * omt ** 2 * tt * c1[0] + 3 * omt * tt ** 2 * c2[0] + tt ** 3 * end[0];
        const y = omt ** 3 * cursor[1] + 3 * omt ** 2 * tt * c1[1] + 3 * omt * tt ** 2 * c2[1] + tt ** 3 * end[1];
        samples.push([x, y]);
      }

      let segLen = 0;
      for (let k = 1; k < samples.length; k++) segLen += distance(samples[k - 1], samples[k]);
      segments.push({ startLength: cumulative, endLength: cumulative + segLen, samples });
      cumulative += segLen;
      cursor = end;
    } else if (cmd === 'Z' && closed) {
      const len = distance(cursor, pathStart);
      segments.push({
        startLength: cumulative,
        endLength: cumulative + len,
        samples: [cursor, pathStart],
      });
      cumulative += len;
      cursor = pathStart;
    }
  }

  return { segments, totalLength: cumulative };
}

/**
 * Arc-length sampler of an SVG path (absolute M/L/C/Z): cubics are flattened into `samplesPerCubic` uniform-parameter
 * steps. The one sampler of the TS runtime — the text-on-path renderers and the arc-fraction law below share it. The
 * PSD parser (`text-path.extractor.ts`, PSD knot parameter → fraction at import) and the Rust engine port
 * (`glyph_layout.rs`) keep their own integrators.
 */
export function buildPathSampler(d: string, closed: boolean, samplesPerCubic = 256): PathSampler {
  const { segments, totalLength: cumulative } = buildSegments(d, closed, samplesPerCubic);

  return {
    totalLength: cumulative,
    closed,
    pointAtLength(s: number): PathPoint {
      if (segments.length === 0) return { x: 0, y: 0, tangent: 0 };
      const target = Math.max(0, Math.min(s, cumulative));
      const seg = segments.find(sg => target >= sg.startLength && target <= sg.endLength) || segments[segments.length - 1];
      let acc = seg.startLength;
      for (let i = 1; i < seg.samples.length; i++) {
        const p0 = seg.samples[i - 1];
        const p1 = seg.samples[i];
        const subLen = distance(p0, p1);
        if (target <= acc + subLen) {
          const t = subLen === 0 ? 0 : (target - acc) / subLen;
          return {
            x: p0[0] + t * (p1[0] - p0[0]),
            y: p0[1] + t * (p1[1] - p0[1]),
            tangent: Math.atan2(p1[1] - p0[1], p1[0] - p0[0]),
          };
        }

        acc += subLen;
      }

      const last = seg.samples[seg.samples.length - 1];
      const prev = seg.samples[seg.samples.length - 2] || last;
      return { x: last[0], y: last[1], tangent: Math.atan2(last[1] - prev[1], last[0] - prev[0]) };
    },
  };
}

/**
 * Where an arc-length fraction of a path lands on the image of that path under the axis scale (sx, sy): the fraction
 * of the image point's arc length, not the same fraction of the new length. An axis scale keeps every Bézier
 * parameter, so the image of a polyline step is the same step of the image polyline — each step's length is just
 * re-measured under the scale. Translation never changes lengths and is not needed. Returns `null` when the map is
 * the identity on fractions (uniform scale, mirror included) or cannot be measured (degenerate scale or path).
 */
export function arcFractionUnderScale(
  d: string,
  closed: boolean,
  sx: number,
  sy: number,
  samplesPerCubic = 256,
): ((fraction: number) => number) | null {
  if (Math.abs(sx) === Math.abs(sy) || !Number.isFinite(sx) || !Number.isFinite(sy) || sx === 0 || sy === 0) return null;
  const { segments, totalLength } = buildSegments(d, closed, samplesPerCubic);
  const before: number[] = [];
  const after: number[] = [];
  for (const segment of segments) {
    for (let i = 1; i < segment.samples.length; i++) {
      const [x0, y0] = segment.samples[i - 1];
      const [x1, y1] = segment.samples[i];
      before.push(Math.hypot(x1 - x0, y1 - y0));
      after.push(Math.hypot((x1 - x0) * sx, (y1 - y0) * sy));
    }
  }

  const scaledLength = after.reduce((sum, len) => sum + len, 0);
  if (!(totalLength > 0) || !(scaledLength > 0)) return null;

  const withinOneLap = (fraction: number): number => {
    const target = fraction * totalLength;
    let acc = 0;
    let accScaled = 0;
    for (let i = 0; i < before.length; i++) {
      if (target <= acc + before[i]) {
        const t = before[i] === 0 ? 0 : (target - acc) / before[i];
        return (accScaled + t * after[i]) / scaledLength;
      }

      acc += before[i];
      accScaled += after[i];
    }

    return 1;
  };

  // A closed path's range may wrap past its start (`pathRangeEnd` > 1): whole laps stay whole, the remainder maps.
  return fraction => {
    const laps = Math.floor(fraction);
    const rest = fraction - laps;
    return rest === 0 ? fraction : laps + withinOneLap(rest);
  };
}
